import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaArrowLeft, FaSearch, FaMedal, FaEye } from "react-icons/fa";
import TableLoader from "Components/Common/Loader/TableLoader";
import UserPointsDetailModal from "./UserPointsDetailModal";

const medalColors = {
  1: "text-yellow-500",
  2: "text-gray-400",
  3: "text-amber-700",
};

const EventLeaderboardAdmin = ({ event, onBack }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  const fetchLeaderboard = (page = 1) => {
    setLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/event/${event._id}/leaderboard`,
        { params: { page, limit: pagination.limit } }
      )
      .then((response) => {
        setLeaderboard(response.data.data || []);
        setPagination(response.data.pagination || {});
      })
      .catch((error) => {
        console.error("Error fetching leaderboard:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (event?._id) {
      fetchLeaderboard();
    }
  }, [event]);

  const filteredLeaderboard = search
    ? leaderboard.filter((entry) => {
        const query = search.toLowerCase();
        const nickName = (entry.nickName || "").toLowerCase();
        const phone = (entry.phoneNumber || entry.phone || "").toLowerCase();
        return nickName.includes(query) || phone.includes(query);
      })
    : leaderboard;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="mx-auto mt-5 w-auto rounded-t-2xl">
      <div className="rounded-2xl bg-white py-5 shadow-sm">
        {/* Header */}
        <div className="flex flex-col items-start justify-between space-y-4 p-5 text-navy-600 shadow-sm md:flex-row md:items-center md:space-y-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center justify-center rounded-full bg-gray-100 p-2 hover:bg-gray-200"
            >
              <FaArrowLeft className="text-gray-600" />
            </button>
            <div>
              <h2 className="text-2xl font-semibold tracking-wide">
                🏆 Event Leaderboard
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {event.name} • {formatDate(event.startDate)} —{" "}
                {formatDate(event.endDate)}
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-full md:w-64">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or mobile..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-[#c51369] focus:outline-none"
            />
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-4">
          <div className="rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 p-3 text-center">
            <p className="text-2xl font-bold text-[#c51369]">
              {pagination.total || 0}
            </p>
            <p className="text-xs text-gray-500">Total Participants</p>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-yellow-50 to-amber-50 p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">
              {leaderboard[0]?.totalPoints || 0}
            </p>
            <p className="text-xs text-gray-500">Highest Score</p>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 p-3 text-center">
            <p className="truncate text-2xl font-bold text-green-600">
              {leaderboard[0]?.nickName || "-"}
            </p>
            <p className="text-xs text-gray-500">
              {leaderboard[0]?.phoneNumber || leaderboard[0]?.phone ? (
                <span className="font-medium text-emerald-700">
                  {leaderboard[0]?.phoneNumber || leaderboard[0]?.phone} •{" "}
                </span>
              ) : null}
              Current Leader
            </p>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 p-3 text-center">
            <p className="text-2xl font-bold capitalize text-blue-600">
              {event.status}
            </p>
            <p className="text-xs text-gray-500">Event Status</p>
          </div>
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className="mb-5 overflow-x-auto">
        {loading ? (
          <TableLoader />
        ) : filteredLeaderboard.length === 0 ? (
          <div className="rounded-b-2xl bg-white py-20 text-center">
            <p className="text-lg text-gray-400">No participants yet</p>
          </div>
        ) : (
          <table className="w-full border text-left text-sm">
            <thead className="bg-[#e0ced7] text-gray-700">
              <tr>
                <th className="border py-2 text-center">Rank</th>
                <th className="border py-2 text-center">Avatar</th>
                <th className="border py-2 text-center">Name</th>
                <th className="border py-2 text-center">Mobile Number</th>
                <th className="border py-2 text-center">Total Points</th>
                <th className="border py-2 text-center">Gang Create</th>
                <th className="border py-2 text-center">Gang Duration</th>
                <th className="border py-2 text-center">Games</th>
                <th className="border py-2 text-center">Gifts Sent</th>
                <th className="border py-2 text-center">Gifts Received</th>
                <th className="border py-2 text-center">Calls</th>
                <th className="border py-2 text-center">Daily Login</th>
                <th className="border py-2 text-center">Last Active</th>
                <th className="border py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeaderboard.map((entry) => (
                <tr
                  key={entry.userId}
                  className={`border-b hover:bg-gray-50 ${
                    entry.rank <= 3 ? "bg-yellow-50/50" : ""
                  }`}
                >
                  <td className="border py-2 text-center">
                    {entry.rank <= 3 ? (
                      <span className="flex items-center justify-center gap-1">
                        <FaMedal
                          className={
                            medalColors[entry.rank] || "text-gray-400"
                          }
                        />
                        <span className="font-bold">#{entry.rank}</span>
                      </span>
                    ) : (
                      <span className="font-medium">#{entry.rank}</span>
                    )}
                  </td>
                  <td className="border py-2 text-center">
                    {entry.selected_Avatar ? (
                      <img
                        src={entry.selected_Avatar}
                        alt=""
                        className="mx-auto h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-xs">
                        ?
                      </div>
                    )}
                  </td>
                  <td className="border py-2 text-center font-medium">
                    {entry.nickName || "Unknown"}
                  </td>
                  <td className="border py-2 text-center font-mono text-xs text-gray-700">
                    {entry.phoneNumber || entry.phone || "-"}
                  </td>
                  <td className="border py-2 text-center">
                    <span className="font-bold text-[#c51369]">
                      {entry.totalPoints}
                    </span>
                  </td>
                  <td className="border py-2 text-center">
                    {entry.pointBreakdown?.gang_create || 0}
                  </td>
                  <td className="border py-2 text-center">
                    {entry.pointBreakdown?.gang_duration || 0}
                  </td>
                  <td className="border py-2 text-center">
                    {entry.pointBreakdown?.individual_game || 0}
                  </td>
                  <td className="border py-2 text-center">
                    {entry.pointBreakdown?.gift_sent || 0}
                  </td>
                  <td className="border py-2 text-center">
                    {entry.pointBreakdown?.gift_collected || 0}
                  </td>
                  <td className="border py-2 text-center">
                    {(entry.pointBreakdown?.audio_call || 0) +
                      (entry.pointBreakdown?.video_call || 0)}
                  </td>
                  <td className="border py-2 text-center">
                    {entry.pointBreakdown?.daily_login || 0}
                  </td>
                  <td className="border py-2 text-center text-xs text-gray-500">
                    {formatDate(entry.lastActivityAt)}
                  </td>
                  <td className="border py-2 text-center">
                    <button
                      onClick={() => {
                        setSelectedUserId(entry.userId);
                        setShowUserDetail(true);
                      }}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#c51369] px-3 py-1 text-xs font-medium text-white transition hover:bg-[#a0104f]"
                      title="View detailed points breakdown"
                    >
                      <FaEye /> Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* User Points Detail Modal */}
      <UserPointsDetailModal
        open={showUserDetail}
        onClose={() => {
          setShowUserDetail(false);
          setSelectedUserId(null);
        }}
        eventId={event?._id}
        userId={selectedUserId}
      />



      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pb-5">
          <button
            disabled={pagination.page <= 1}
            onClick={() => fetchLeaderboard(pagination.page - 1)}
            className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => fetchLeaderboard(pagination.page + 1)}
            className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default EventLeaderboardAdmin;
